import { useEffect } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ACCOUNTS_WEB_URL, ApiError } from "../api/client";
import { useLogout, useMe } from "../api/me";
import { Skeleton } from "../ui";
import { assignLocation } from "./location";

const navClass = ({ isActive }: { isActive: boolean }) =>
  [
    "rounded-pill px-3 py-1.5 text-sm font-semibold transition-colors",
    isActive
      ? "bg-brand-tint text-brand-dark"
      : "text-slate-600 hover:bg-white/70 hover:text-brand-dark",
  ].join(" ");

/** Credits available, or a bar while `/v1/me` is in flight. Never a zero the
 *  user did not earn (see `Skeleton`); a dash when Accounts could not be
 *  reached and the API reports `null`. */
export function BalanceChip() {
  const { data, isPending } = useMe();
  const topUp = `${ACCOUNTS_WEB_URL}/account`;
  return (
    <a
      href={topUp}
      className="tb-pill tb-pill-purple inline-flex items-center gap-1.5 no-underline"
      title="Top up credits in Intelibo Accounts"
      aria-busy={isPending}
    >
      {data ? (
        <span data-testid="balance">
          {data.balance.available === null
            ? "— credits"
            : `${data.balance.available.toLocaleString("en-US")} credits`}
        </span>
      ) : (
        <Skeleton width={72} height={12} />
      )}
    </a>
  );
}

export function UserMenu() {
  const { data } = useMe();
  const logout = useLogout();
  if (!data) return null;
  return (
    <details className="relative">
      <summary
        className="tb-btn tb-btn-ghost tb-btn-sm cursor-pointer list-none"
        aria-label="User menu"
      >
        {data.user.display_name || data.user.email}
      </summary>
      <div
        className="tb-card tb-card-plain absolute right-0 mt-2 flex w-56 flex-col gap-1 p-2 text-sm"
        role="menu"
      >
        <div className="px-2 py-1 text-slate-500">
          <div className="truncate font-semibold text-slate-800">
            {data.user.email}
          </div>
          <div className="truncate">{data.org.name}</div>
        </div>
        <a
          role="menuitem"
          className="rounded px-2 py-1.5 no-underline hover:bg-slate-100"
          href={`${ACCOUNTS_WEB_URL}/account`}
        >
          Account, receipts and top-up
        </a>
        <button
          type="button"
          role="menuitem"
          className="rounded px-2 py-1.5 text-left hover:bg-slate-100 disabled:opacity-60"
          disabled={logout.isPending}
          onClick={() =>
            logout.mutate(undefined, {
              onSuccess: ({ end_session_url }) =>
                assignLocation(end_session_url),
            })
          }
        >
          Log out
        </button>
      </div>
    </details>
  );
}

/** Header + page outlet. Sends an unauthenticated visitor to `/login`,
 *  remembering where they were. */
export function Shell() {
  const { data, error } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const unauthenticated = error instanceof ApiError && error.status === 401;

  useEffect(() => {
    if (!unauthenticated) return;
    const here = location.pathname + location.search;
    navigate(`/login?return_to=${encodeURIComponent(here)}`, {
      replace: true,
    });
  }, [unauthenticated, location.pathname, location.search, navigate]);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/80 backdrop-blur">
        {/* On a phone the nav takes its own row and scrolls sideways; the balance and
            the user menu stay in reach instead of being pushed off the edge. */}
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
          <Link
            to="/ads"
            className="gradient-text text-xl font-extrabold tracking-tight no-underline"
            title="Your ads"
          >
            TrueReel
          </Link>
          <nav
            className="order-last flex w-full items-center gap-1 overflow-x-auto whitespace-nowrap md:order-none md:w-auto"
            aria-label="Primary"
          >
            <NavLink to="/ads" className={navClass}>
              Ads
            </NavLink>
            <NavLink to="/new" className={navClass}>
              New ad
            </NavLink>
            <NavLink to="/media" className={navClass}>
              Media
            </NavLink>
            {data?.features?.assetlib ? (
              <NavLink
                to="/assets"
                className={(s) =>
                  navClass({
                    isActive:
                      s.isActive || location.pathname === "/help/3d-library",
                  })
                }
              >
                Objects
              </NavLink>
            ) : null}
            <NavLink to="/brand" className={navClass}>
              Brand
            </NavLink>
            <NavLink to="/agent" className={navClass}>
              Your GPU
            </NavLink>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <BalanceChip />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
